import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, widgetKeyHeaders } from '../config/api';
import { hashContextKeys } from '../utils/context';

const FETCH_DEBOUNCE_MS = 600;
const BACKOFF_SEQUENCE = [250, 500, 1000];

const buildParams = (sessionId, contextKeys) => {
  const params = new URLSearchParams();
  if (sessionId) params.set('session_id', sessionId);
  if (contextKeys && contextKeys.length) params.set('context_keys', contextKeys.join(','));
  return params.toString();
};

const formatFallbackTools = (tools = []) => {
  const readyTools = tools.filter(tool => tool.ready);
  const lockedTools = tools.filter(tool => !tool.ready);
  return {
    tools,
    readyTools,
    lockedTools,
    planTier: 'fallback',
    toolLimit: tools.length,
    currentCount: readyTools.length,
    contextUsed: [],
    isFallback: true,
    loading: false,
    error: null
  };
};

export const useToolsFeed = ({
  companyIdSlug,
  sessionId,
  contextKeys = [],
  isTrialPlan,
  fallbackToolsProvider,
  vertical = 'business' // Add vertical parameter
}) => {
  const [state, setState] = useState(() => formatFallbackTools(fallbackToolsProvider?.() || []));
  const [celebration, setCelebration] = useState(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const etagRef = useRef(null);
  const readyIdsRef = useRef(new Set());
  const fetchInFlightRef = useRef(false);
  const backoffIndexRef = useRef(0);
  const contextHash = useMemo(() => hashContextKeys(contextKeys), [contextKeys]);

  const applyFallback = useCallback(() => {
    const fallback = fallbackToolsProvider?.() || [];
    setState(formatFallbackTools(fallback));
    readyIdsRef.current = new Set(fallback.filter(tool => tool.ready).map(tool => tool.id));
  }, [fallbackToolsProvider]);

  const cleanupRequest = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    fetchInFlightRef.current = false;
  }, []);

  const fetchTools = useCallback(async () => {
    cleanupRequest();

    // Only fetch from backend for MentorMind/coaching vertical
    // For other verticals (business/opsiq, hvac), use fallback tools only
    // Note: Even trial plans should fetch frameworks for MentorMind
    const shouldUseFallback = !companyIdSlug || (vertical !== 'mentormind' && vertical !== 'coaching');
    
    if (shouldUseFallback) {
      applyFallback();
      return;
    }

    const params = buildParams(sessionId, contextKeys);
    const url = api.coachTools(companyIdSlug, params);
    const controller = new AbortController();
    abortRef.current = controller;
    fetchInFlightRef.current = true;

    setState(prev => ({
      ...prev,
      loading: prev.isFallback || prev.loading || prev.tools.length === 0,
      error: null
    }));

    try {
      const headers = { ...widgetKeyHeaders() };
      if (etagRef.current) {
        headers['If-None-Match'] = etagRef.current;
      }
      const res = await fetch(url, { headers, signal: controller.signal });

      if (res.status === 304) {
        setState(prev => ({ ...prev, loading: false, error: null }));
        fetchInFlightRef.current = false;
        return;
      }

      if (res.status === 403) {
        const body = await res.json().catch(() => ({}));
        if (body?.code === 'plan_required') {
          applyFallback();
          fetchInFlightRef.current = false;
          return;
        }
        throw new Error(body?.error || 'Plan upgrade required');
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed to load tools (${res.status})`);
      }

      const data = await res.json();
      const backendTools = Array.isArray(data?.tools) ? data.tools : [];

      // Merge preset/fallback tools with backend tools (preset tools take precedence by ID)
      const fallbackTools = fallbackToolsProvider?.() || [];
      const fallbackToolIds = new Set(fallbackTools.map(t => t.id));
      const backendToolIds = new Set(backendTools.map(t => t.id));
      
      // Add backend tools that don't conflict with preset tools
      const mergedTools = [
        ...fallbackTools, // Preset tools first (static)
        ...backendTools.filter(t => !fallbackToolIds.has(t.id)) // Backend tools that aren't preset
      ];
      
      const readyTools = mergedTools.filter(tool => tool.ready);
      const lockedTools = mergedTools.filter(tool => !tool.ready);
      const nextReadyIds = new Set(readyTools.map(tool => tool.id));
      const previousReadyIds = readyIdsRef.current;
      readyIdsRef.current = nextReadyIds;

      let unlockedTool = null;
      readyTools.forEach(tool => {
        if (!previousReadyIds.has(tool.id)) {
          unlockedTool = tool;
        }
      });

      etagRef.current = res.headers.get('ETag');
      backoffIndexRef.current = 0;

      setState({
        tools: mergedTools,
        readyTools,
        lockedTools,
        planTier: data.plan_tier || 'pro',
        toolLimit: data.tool_limit ?? mergedTools.length,
        currentCount: data.current_count ?? readyTools.length,
        contextUsed: data.context_used || [],
        isFallback: false,
        loading: false,
        error: null
      });

      if (unlockedTool) {
        setCelebration({
          id: unlockedTool.id,
          name: unlockedTool.name,
          icon: unlockedTool.icon
        });
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        fetchInFlightRef.current = false;
        return;
      }

      const message = error?.message || 'Unable to load framework tools';
      setState(prev => ({
        ...prev,
        loading: false,
        error: message
      }));

      const backoff = BACKOFF_SEQUENCE[Math.min(backoffIndexRef.current, BACKOFF_SEQUENCE.length - 1)];
      backoffIndexRef.current += 1;
      debounceRef.current = window.setTimeout(() => {
        fetchTools();
      }, backoff);
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [applyFallback, cleanupRequest, companyIdSlug, contextKeys, isTrialPlan, sessionId, vertical]);

  const scheduleFetch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      fetchTools();
    }, FETCH_DEBOUNCE_MS);
  }, [fetchTools]);

  useEffect(() => {
    scheduleFetch();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      cleanupRequest();
    };
  }, [scheduleFetch, cleanupRequest, contextHash]);

  const refetch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchTools();
  }, [fetchTools]);

  return {
    ...state,
    celebration,
    refetch
  };
};

export default useToolsFeed;

